import { DirectUpload } from "@rails/activestorage"

class PhotoUploader {
  constructor() {
    this.queue = new Map(); // Map of UUID -> upload info
    this.uploadUrl = '/rails/active_storage/direct_uploads'; // Default Rails direct upload URL
  }

  /**
   * Add a file to the upload queue
   * @param {File} file - The file to upload
   * @param {Object} options - Upload options
   * @returns {string} UUID for tracking this upload
   */
  addToQueue(file, options = {}) {
    const uuid = this.generateUUID();

    const uploadInfo = {
      uuid,
      file,
      status: 'queued', // queued, uploading, completed, failed
      progress: 0,
      error: null,
      result: null,
      callbacks: {
        onProgress: options.onProgress || (() => {}),
        onComplete: options.onComplete || (() => {}),
        onError: options.onError || (() => {})
      }
    };

    this.queue.set(uuid, uploadInfo);

    // Start upload immediately (could be modified to batch uploads later)
    this.startUpload(uuid);

    return uuid;
  }

  /**
   * Start uploading a file by UUID
   * @param {string} uuid - The UUID of the file to upload
   */
  async startUpload(uuid) {
    // await new Promise(resolve => setTimeout(resolve, 30000));
    const uploadInfo = this.queue.get(uuid);
    if (!uploadInfo || uploadInfo.status !== 'queued') {
      return;
    }

    uploadInfo.status = 'uploading';

    try {
      const directUpload = new DirectUpload(uploadInfo.file, this.uploadUrl, {
        directUploadWillStoreFileWithXHR: (request) => {
          request.upload.addEventListener('progress', (event) => {
            if (event.lengthComputable) {
              uploadInfo.progress = Math.round((event.loaded / event.total) * 100);
              uploadInfo.callbacks.onProgress(uploadInfo.progress, uploadInfo);
            }
          });
        }
      });

      const result = await new Promise((resolve, reject) => {
        directUpload.create((error, blob) => {
          if (error) {
            reject(error);
          } else {
            resolve(blob);
          }
        });
      });

      uploadInfo.status = 'completed';
      uploadInfo.result = result;
      uploadInfo.progress = 100;
      uploadInfo.callbacks.onComplete(result, uploadInfo);

      console.log(`Photo upload completed for UUID ${uuid}:`, result);

      // Notify backend about the completed upload
      this.notifyBackendUploadComplete(uuid, result);

    } catch (error) {
      uploadInfo.status = 'failed';
      uploadInfo.error = error;
      uploadInfo.callbacks.onError(error, uploadInfo);

      console.error(`Photo upload failed for UUID ${uuid}:`, error);
    }
  }

  /**
   * Notify the backend that an upload is complete
   * @param {string} uuid - The UUID of the upload
   * @param {Object} blob - The blob result from DirectUpload
   */
  async notifyBackendUploadComplete(uuid, blob) {
    try {
      const response = await fetch('/api/photo_uploads/complete', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({
          uuid: uuid,
          blob_id: blob.id,
          signed_id: blob.signed_id
        })
      });

      if (response.ok) {
        console.log(`Backend notified of upload completion for UUID ${uuid}`);
      } else {
        console.error(`Failed to notify backend for UUID ${uuid}:`, response.statusText);
      }
    } catch (error) {
      console.error(`Error notifying backend for UUID ${uuid}:`, error);
    }
  }

  /**
   * Get upload status by UUID
   * @param {string} uuid - The UUID to check
   * @returns {Object|null} Upload info or null if not found
   */
  getStatus(uuid) {
    return this.queue.get(uuid) || null;
  }

  /**
   * Get all uploads with a specific status
   * @param {string} status - The status to filter by
   * @returns {Array} Array of upload info objects
   */
  getByStatus(status) {
    return Array.from(this.queue.values()).filter(upload => upload.status === status);
  }

  /**
   * Remove an upload from the queue
   * @param {string} uuid - The UUID to remove
   */
  remove(uuid) {
    this.queue.delete(uuid);
  }

  /**
   * Clear completed uploads from the queue
   */
  clearCompleted() {
    for (const [uuid, uploadInfo] of this.queue.entries()) {
      if (uploadInfo.status === 'completed') {
        this.queue.delete(uuid);
      }
    }
  }

  /**
   * Generate a UUID v4
   * @returns {string} UUID
   */
  generateUUID() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c == 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  /**
   * Get queue size
   * @returns {number} Number of items in queue
   */
  size() {
    return this.queue.size;
  }

  /**
   * Clear all uploads from the queue
   */
  clear() {
    this.queue.clear();
  }
}

export default PhotoUploader;
